import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  useListAuthEvents,
  getListAuthEventsQueryKey,
  type AuthEvent,
  type AuthEventFailureSummary,
} from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RefreshCw } from "lucide-react";

const OUTCOME_LABEL: Record<string, string> = {
  sent: "Sent",
  delivery_failed: "Delivery failed",
  provider_not_configured: "Provider not configured",
  rate_limited: "Rate limited",
  unknown_number: "Unknown number",
  verified: "Verified",
  invalid_code: "Invalid code",
  invalid_format: "Invalid format",
  expired: "Expired",
  too_many_attempts: "Too many attempts",
  user_not_found: "User not found",
  no_active_code: "No active code",
  race_lost: "Race lost",
};

function outcomeBadgeVariant(
  outcome: string,
): "default" | "secondary" | "destructive" | "outline" {
  if (outcome === "verified" || outcome === "sent") return "default";
  if (
    outcome === "delivery_failed" ||
    outcome === "provider_not_configured" ||
    outcome === "too_many_attempts"
  ) {
    return "destructive";
  }
  if (
    outcome === "invalid_code" ||
    outcome === "expired" ||
    outcome === "rate_limited" ||
    outcome === "no_active_code" ||
    outcome === "invalid_format" ||
    outcome === "user_not_found" ||
    outcome === "race_lost"
  ) {
    return "secondary";
  }
  return "outline";
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const REPEATED_FAILURE_THRESHOLD = 3;

export default function LoginActivity() {
  const { user: me, isLoading: meLoading } = useAuth();
  const [, setLocation] = useLocation();
  const isAdmin = me?.role === "super_admin" || me?.role === "admin";
  const queryClient = useQueryClient();

  const [sinceHours, setSinceHours] = useState<string>("24");
  const params = { limit: 200, sinceHours: Number(sinceHours) };

  useEffect(() => {
    if (!meLoading && me && !isAdmin) {
      setLocation("/");
    }
  }, [meLoading, me, isAdmin, setLocation]);

  const { data, isLoading, isFetching } = useListAuthEvents(params, {
    query: {
      queryKey: getListAuthEventsQueryKey(params),
      enabled: isAdmin,
      refetchInterval: 30000,
    },
  });

  if (!meLoading && me && !isAdmin) {
    return (
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Login Activity</h1>
        <p className="text-muted-foreground">
          You don't have permission to view login activity.
        </p>
      </div>
    );
  }

  const events: AuthEvent[] = data?.events ?? [];
  const failures: AuthEventFailureSummary[] = (data?.failuresByNumber ?? []).filter(
    (f) => f.failedAttempts >= REPEATED_FAILURE_THRESHOLD,
  );

  const deliveryProblem = events.find(
    (e) =>
      e.outcome === "delivery_failed" || e.outcome === "provider_not_configured",
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Login Activity</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Recent OTP requests and verifications. Use this to spot delivery
            failures and suspicious sign-in attempts.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={sinceHours} onValueChange={setSinceHours}>
            <SelectTrigger className="w-40" data-testid="since-hours">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last hour</SelectItem>
              <SelectItem value="6">Last 6 hours</SelectItem>
              <SelectItem value="24">Last 24 hours</SelectItem>
              <SelectItem value="72">Last 3 days</SelectItem>
              <SelectItem value="168">Last 7 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={() =>
              queryClient.invalidateQueries({
                queryKey: getListAuthEventsQueryKey(params),
              })
            }
            data-testid="refresh-activity"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {deliveryProblem && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-destructive" />
            <div>
              <CardTitle className="text-base text-destructive">
                WhatsApp delivery problems detected
              </CardTitle>
              <CardDescription>
                {deliveryProblem.outcome === "provider_not_configured"
                  ? "Twilio credentials are missing — set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, and TWILIO_WHATSAPP_FROM to enable real OTP delivery."
                  : `Recent send failed with: ${deliveryProblem.error ?? "unknown error"}`}
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Numbers with repeated failures</CardTitle>
          <CardDescription>
            Numbers with {REPEATED_FAILURE_THRESHOLD} or more failed attempts in
            the selected window.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Failed attempts</TableHead>
                <TableHead>Last attempt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ) : failures.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="h-16 text-center text-muted-foreground"
                  >
                    No numbers with repeated failures.
                  </TableCell>
                </TableRow>
              ) : (
                failures.map((f) => (
                  <TableRow
                    key={f.whatsappNumber}
                    data-testid={`failure-row-${f.whatsappNumber}`}
                  >
                    <TableCell className="font-mono text-xs">
                      {f.whatsappNumber}
                    </TableCell>
                    <TableCell>
                      <Badge variant="destructive">{f.failedAttempts}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatTime(f.lastAt)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent events</CardTitle>
          <CardDescription>
            Showing the last {events.length} OTP request and verification events.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(6)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="h-24 text-center text-muted-foreground"
                  >
                    No login activity in this window.
                  </TableCell>
                </TableRow>
              ) : (
                events.map((e) => (
                  <TableRow key={e.id} data-testid={`event-row-${e.id}`}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatTime(e.createdAt)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {e.whatsappNumber}
                    </TableCell>
                    <TableCell className="text-sm">
                      {e.eventType === "request_otp" ? "Request" : "Verify"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={outcomeBadgeVariant(e.outcome)}>
                        {OUTCOME_LABEL[e.outcome] ?? e.outcome}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {e.error ?? ""}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
